/**
 * Copyright © 2026 Adnuntius AS.
 */
import {SignalResourceListBindingService} from '../common/controller/signal-resource-list-binding.service';
import {DestroyRef, inject, Component, Inject, Input, signal} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {UIRouter} from '@uirouter/core';

import template from './library-creative-edit-page.component.html';
import {SignalAdminResourceEditPageBase} from '../common/controller/signal-admin-resource-edit-page-base';
import {
  LibraryCreativeEditBehavior,
  LibraryCreativeEditBehaviorService
} from './library-creative-edit-setup';
import {NavigationHistoryService} from '../../components/util/navigation-history.service';
import {TargetingEditorModalService} from '../targeting/targeting-editor-modal.service';
import {CreativeRetailProductModalService} from '../creative/creative-retail-product-modal.service';
import {TeamApiService} from '../admin/team/team-api.service';
import {AdvertiserApiService} from '../advertiser/advertiser-api.service';
import {CreativeApiService} from '../creative/creative-api.service';

import {ProductFeedApiService} from '../common/controller/product-feed-api.service';
import {AdnRollbarService} from '../../components/util/adn-rollbar.service';
import {AssetService} from '../creative/asset/asset.service';
import {CreativeHelper} from '../creative/creative-helper.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';

import {compareById} from '../common/util/object-id-comparison';
import {LibraryCreativeApiService, LibraryCreativeModel} from './library-creative-api.service';
import {ADVERTISING_SHARED} from '../advertising/advertising-shared';
import {SoloVisibilityInfoComponent} from '../common/directives/solo-visibility-info-component';

@Component({
  standalone: true,
  imports: [...ADVERTISING_SHARED, SoloVisibilityInfoComponent],
  selector: 'adn-library-creative-edit-page',
  template
})
export class LibraryCreativeEditPageComponent extends SignalAdminResourceEditPageBase<LibraryCreativeModel> {
  private readonly listLifetime = inject(DestroyRef);
  [key: string]: any;

  private _creativeTypeDirect: any;
  private _lineItemInfoDirect: any;
  private setupInitialized = false;
  private libraryCreativeInitialized = false;
  private creativeBehavior: LibraryCreativeEditBehavior | null = null;
  private readonly allResolvedState = signal(false);

  activeTab: 'design' | 'co2' | 'linked' = 'design';
  get hasItems(): boolean { return !!this.listState?.items().length; }
  get relCreativesCount(): number { return this.listState?.totalCount() || 0; }
  showingCo2 = false;
  showDesignDetails = false;
  showThirdPartyDetails = false;
  showVariables = false;
  readonly codeMirrorOpts = {lineNumbers: true, mode: 'htmlmixed', smartIndent: false};

  get displayAllResolved(): boolean {
    return this.allResolvedState();
  }

  get allResolved(): boolean { return this.allResolvedState(); }
  set allResolved(value: boolean) { this.allResolvedState.set(!!value); }

  @Input()
  set creativeType(value: any) {
    this._creativeTypeDirect = value;
  }

  get creativeType(): any {
    return this._creativeTypeDirect;
  }

  @Input()
  set lineItemInfo(value: any) {
    this._lineItemInfoDirect = value;
  }

  get lineItemInfo(): any {
    return this._lineItemInfoDirect;
  }

  constructor(
    @Inject(LocalNetworkProfile) private readonly libraryCreativeNetworkProfile: any,
    private readonly translate: TranslateService,
    private readonly teamApi: TeamApiService,
    private readonly productFeedApi: ProductFeedApiService,
    @Inject(CreativeHelper) private readonly CreativeHelper: any,
    private readonly listBinding: SignalResourceListBindingService,
    @Inject(AdnRollbarService) private readonly Rollbar: any,
    @Inject(AssetService) private readonly Asset: any,
    @Inject(CreativeApiService) private readonly Creative: CreativeApiService,
    @Inject(LibraryCreativeApiService) private readonly LibraryCreative: LibraryCreativeApiService,
    private readonly libraryCreativeApi: LibraryCreativeApiService,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly advertiserApi: AdvertiserApiService,
    private readonly targetingEditorModalService: TargetingEditorModalService,
    private readonly creativeRetailProductModalService: CreativeRetailProductModalService,
    private readonly uiRouter: UIRouter,
    private readonly navigationHistory: NavigationHistoryService,
    private readonly creativeBehaviorService: LibraryCreativeEditBehaviorService
  ) {
    super(libraryCreativeApi, 'libraryCreative');
  }

  override ngOnDestroy(): void {
    this.creativeBehavior?.destroy();
    super.ngOnDestroy();
  }

  onFieldChange(): void {
    this.markExternalFormDirty();
  }

  selectTab(tab: 'design' | 'co2' | 'linked'): void {
    this.activeTab = tab;
    if (tab === 'co2') {
      this.showingCo2 = true;
    }
  }

  readonly compareById = compareById;

  trackById(_index: number, item: any): string {
    return item?.id;
  }

  getTextareaRows(comp: any): number {
    const rawRows = comp.maxLength / 75 + 1;
    if (rawRows < 2) {
      return 1;
    }
    if (rawRows > 10) {
      return 10;
    }
    return Math.floor(rawRows);
  }

  protected override afterEditingModelReset(): void {
    this.ensureSetup();
    if (!this.libraryCreativeInitialized) {
      this.libraryCreativeInitialized = true;
      this.creativeBehavior?.afterInit();
    } else {
      this.creativeBehavior?.afterInitAfterSave();
    }
  }

  onAdvertiserMismatchFixed(saved: any): void {
    if (saved) {
      this.editingModel.advertiser = saved.advertiser;
      this.editingModel.validationWarnings = saved.validationWarnings;
    }
    this.state.syncModel({...this.state.model(), ...saved});
    this.creativeBehavior?.afterInitAfterSave();
    this.publishModelUpdate();
  }

  protected override afterSave(resource: LibraryCreativeModel): void {
    this.creativeBehavior?.afterSave(resource);
  }

  protected override prepareForSubmit(_model: LibraryCreativeModel): void {
    this.prepareLibraryCreativeForSubmit();
  }

  prepareLibraryCreativeForSubmit(): void {
    this.creativeBehavior?.beforeSubmit();
  }

  publishModelUpdate(): void {
    this.state.syncModel({...this.editingModel});
  }

  applyBehaviorSave(resource: LibraryCreativeModel): void {
    this.saveDirect(resource);
  }

  private ensureSetup(): void {
    if (this.setupInitialized || !this.model) {
      return;
    }
    this.setupInitialized = true;

    this.creativeBehavior = this.creativeBehaviorService.initialize(this, {
      model: this.model,
      readOnly: this.readOnly,
      creativeType: this.creativeType,
      lineItemInfo: this.lineItemInfo,
      navigationHistory: this.navigationHistory,
      routeParams: this.uiRouter.globals.params,
      translate: this.translate,
      teamApi: this.teamApi,
      productFeedApi: this.productFeedApi,
      CreativeHelper: this.CreativeHelper,
      listBinding: this.listBinding,
      listLifetime: this.listLifetime,
      Rollbar: this.Rollbar,
      Asset: this.Asset,
      Creative: this.Creative,
      LibraryCreative: this.LibraryCreative,
      libraryCreativeApi: this.libraryCreativeApi,
      LocalNetworkProfile: this.libraryCreativeNetworkProfile,
      LocalUserPermissions: this.LocalUserPermissions,
      Advertiser: this.advertiserApi,
      TargetingEditorModalService: this.targetingEditorModalService,
      creativeRetailProductModalService: this.creativeRetailProductModalService
    });
  }
}
